/**
 * RS Customizer — Bead Mine Kids
 * SVG path-based bead renderer with sequential fill (left→right index).
 * Letters centered on path; blank beads fill remaining space with continuous
 * sequential indices (0, 1, 2…) across left segment, spacers, right segment.
 * Requires window.RSBeadPath (rs-bead-path-utils.js).
 *
 * MTO input validation is handled by rs-product-mto-input.js. ATC gating is managed by rs-product-core.js.
 * This file handles ONLY the visual preview.
 */
(function () {
  'use strict';

  const METAOBJECT_TYPE = 'customizer_bead_mine_kids_set';
  const LIQUID_VIEW = 'metaobject-bead-mine-kids';
  const BEAD_MINE_KIDS_SHADOW = { distance: 8, opacity: 0.2, blur: 1.5 };

  let _injectedSlideId = null;
  let _baseConfig = null;
  let _letters = {};
  let _backgroundsData = null;
  let _currentVariantTitle = null;
  let _beadConfig = null;
  let _resizeTimer = null;

  const CONFIG = {
    svgContainerClass: 'bead-path-svg-container',
    beadContainerClass: 'bead-path-bead-container',
    letterBeadClass: 'bead-path-letter-bead',
    spacerBeadClass: 'bead-path-spacer-bead',
    blankBeadClass: 'bead-path-blank-bead',
  };

  // ─── Helpers ────────────────────────────────────────────────────

  function _getMtoText() {
    const fields = document.querySelectorAll('[data-mto-field]');
    let text = '';
    fields.forEach(function (field) { text += field.value; });
    return text;
  }

  function _normalizeTitle(title) {
    return (title || '').replace(/['''']/g, '').replace(/\s+/g, ' ').trim().toLowerCase();
  }

  // ─── Data fetching ──────────────────────────────────────────────

  function _fetchBaseConfig() {
    return fetch(window.RSProduct.config.productUrl + '?view=' + LIQUID_VIEW)
      .then(function (response) { return response.json(); });
  }

  function _fetchLetters() {
    if (!window.RSGraphQL || !window.RSGraphQL.fetchCustomizerLetters) {
      return Promise.resolve({});
    }
    return window.RSGraphQL.fetchCustomizerLetters(METAOBJECT_TYPE, {})
      .then(function (raw) {
        if (!Array.isArray(raw)) return {};
        const normalizedProductTitle = _normalizeTitle((_baseConfig && _baseConfig.product_title) || '');
        const result = {};
        raw.forEach(function (l) {
          if (!l || !l.key || !l.image) return;
          if (normalizedProductTitle && !_normalizeTitle(l.name || '').includes(normalizedProductTitle)) return;
          result[l.key] = {
            url: l.image.url,
            width: l.image.width || 150,
            height: l.image.height || 150,
            aspectRatio: (l.image.width && l.image.height)
              ? l.image.width / l.image.height : 1,
            left_recess: l.left_recess || 0,
            right_recess: l.right_recess || 0,
          };
        });
        return result;
      });
  }

  // ─── Background management ──────────────────────────────────────

  function _getBackgroundUrl() {
    if (!_backgroundsData) return null;
    if (_backgroundsData['default']) return _backgroundsData['default'];
    const keys = Object.keys(_backgroundsData).filter(function (key) { return _backgroundsData[key]; });
    return keys.length > 0 ? _backgroundsData[keys[0]] : null;
  }

  function _getBackgroundForVariant(variantTitle) {
    if (!_backgroundsData || !variantTitle) return _getBackgroundUrl();
    const normalizedTitle = variantTitle.toLowerCase();
    let match = null;
    Object.entries(_backgroundsData).forEach(function (pair) {
      if (pair[0].toLowerCase() === normalizedTitle || normalizedTitle.includes(pair[0].toLowerCase())) match = pair[1];
    });
    return match || _getBackgroundUrl();
  }

  // ─── Bead config ────────────────────────────────────────────────

  function _buildBeadConfig() {
    if (!_baseConfig || !_baseConfig.settings) return null;
    const settings = _baseConfig.settings;
    return {
      svg_guide_content: settings.svg_guide_content,
      svg_view_box_width: settings.svg_view_box_width || 1110,
      svg_view_box_height: settings.svg_view_box_height || 880,
      bead_height: settings.bead_height || 90,
      letter_height: settings.letter_height || 90,
      bead_width: settings.bead_width || null,
      letter_width: settings.letter_width || null,
      letter_spacing: settings.letter_spacing || 0,
      bead_spacing: settings.bead_spacing || 0,
      y_offset: settings.y_offset || 0,
      random_offset: settings.random_offset === true || settings.random_offset === 'true',
      offset_factor: settings.offset_factor || 0,
      light_source_angle: settings.light_source_angle || 0,
      stroke_width: settings.stroke_width || 0,
      stroke_color: settings.stroke_color || 'rgba(0,0,0,0)',
      bead_rotation_offset: 0,
      beads: settings.beads || [],
      spacer: settings.spacer || {},
      letters: _letters,
    };
  }

  // ─── Canvas helpers ─────────────────────────────────────────────

  function _getCustomizerView() {
    if (!_injectedSlideId) return null;
    const slide = document.querySelector('[data-rs-injected="' + _injectedSlideId + '"]');
    if (!slide) return null;
    return slide.querySelector('.customizer-view');
  }

  // ─── Left/right sequential fill ─────────────────────────────────

  function _renderLeftFill(bc, beadCont, pathEl, occ, scale, pathLen, scaledLen, startIdx) {
    const BP = window.RSBeadPath;
    const dims = BP.calculateBeadDimensions(bc);
    const spacing = dims.width * scale * 0.95;
    let idx = startIdx;
    const sorted = occ.slice().sort(function (a, b) { return a.position - b.position; });

    if (!sorted.length) return idx;
    const first = sorted[0];
    const firstStart = first.start !== undefined ? first.start : first.position - (first.size / 2 / pathLen);
    if (firstStart <= 0) return idx;

    const segLen = firstStart * scaledLen;
    const cnt = Math.min(500, Math.floor(segLen / spacing));
    if (cnt <= 0) return idx;
    const sp = segLen / cnt;

    for (var i = 0; i < cnt; i++) {
      const pos = ((i + 0.5) * sp) / scaledLen;
      BP.renderBlankBead(beadCont, pathEl, pos * pathLen, bc, CONFIG.blankBeadClass, idx);
      idx++;
    }
    return idx;
  }

  function _renderRightFill(bc, beadCont, pathEl, occ, scale, pathLen, scaledLen, startIdx) {
    const BP = window.RSBeadPath;
    const dims = BP.calculateBeadDimensions(bc);
    const spacing = dims.width * scale * 0.95;
    let idx = startIdx;
    const sorted = occ.slice().sort(function (a, b) { return a.position - b.position; });

    if (!sorted.length) return idx;
    const last = sorted[sorted.length - 1];
    const lastEnd = last.end !== undefined ? last.end : last.position + (last.size / 2 / pathLen);
    if (lastEnd >= 1) return idx;

    const segStart = lastEnd;
    const segLen = (1 - segStart) * scaledLen;
    const cnt = Math.min(500, Math.floor(segLen / spacing));
    if (cnt <= 0) return idx;
    const sp = segLen / cnt;

    for (var i = 0; i < cnt; i++) {
      const rel = ((i + 0.5) * sp) / scaledLen;
      const pos = segStart + rel;
      BP.renderBlankBead(beadCont, pathEl, pos * pathLen, bc, CONFIG.blankBeadClass, idx);
      idx++;
    }
    return idx;
  }

  // ─── Main render ─────────────────────────────────────────────────

  function _renderBeadsWithText(container, text, bc) {
    const BP = window.RSBeadPath;
    if (!BP || !container || !bc) return;

    const svg = BP.createSVGContainer(container, bc, CONFIG.svgContainerClass);
    const pathEl = BP.createSVGPath(svg, bc.svg_guide_content);
    if (!pathEl) return;

    const beadCont = BP.createBeadContainer(container, CONFIG.beadContainerClass);
    BP.clearBeadsInContainer(beadCont, [
      CONFIG.letterBeadClass, CONFIG.spacerBeadClass, CONFIG.blankBeadClass,
    ]);

    const scale = BP.getResponsiveScale(container, bc);
    const pathLen = pathEl.getTotalLength();
    const scaledLen = pathLen * scale;
    const dims = BP.calculateBeadDimensions(bc);
    const beadSpacing = dims.width * scale * 0.95;

    let validLetters = [];
    if (text && text.trim()) {
      validLetters = text.toUpperCase().split('').filter(function (c) { return _letters[c]; });
    }

    // No valid letters → sequential blank fill across entire path
    if (!validLetters.length) {
      const cnt = Math.floor(scaledLen / beadSpacing);
      for (var i = 0; i < cnt; i++) {
        const pathPos = (i * beadSpacing) / scale + (dims.width / 2);
        BP.renderBlankBead(beadCont, pathEl, pathPos, bc, CONFIG.blankBeadClass, i);
      }
      BP.applyDropShadowToContainer(container, Object.assign({}, bc, { shadow: BEAD_MINE_KIDS_SHADOW }));
      return;
    }

    const renderer = BP.getRenderer(bc);
    const letterDims = validLetters.map(function (l) {
      const ld = _letters[l];
      if (ld) {
        const d = renderer.calculateLetterDimensions(ld, bc);
        return { width: d.width, height: d.height, left_recess: ld.left_recess || 0, right_recess: ld.right_recess || 0 };
      }
      return { width: (bc.bead_width || 80) * 1.2, height: bc.letter_height || (bc.bead_width || 80) * 1.2, left_recess: 0, right_recess: 0 };
    });

    const cp = 0.5;
    const totLW = letterDims.reduce(function (sum, d) { return sum + d.width; }, 0);
    const totRW = letterDims.reduce(function (sum, d) { return sum + d.left_recess + d.right_recess; }, 0);
    const spDims = BP.calculateBeadDimensions(bc);
    const spBW = spDims.width;
    const spPerGap = 2;
    const bSpc = bc.bead_spacing || 0;
    const totSpW = (validLetters.length - 1) * spPerGap * spBW;
    const totBSAdj = bSpc * (validLetters.length - 1) * (spPerGap - 1);
    let totCW = totLW + totSpW + totRW + totBSAdj;
    const lSpVal = (totCW * (bc.letter_spacing || 0)) / 100;
    totCW += lSpVal;
    const finCW = totCW * scale;
    const lGap = validLetters.length > 1 ? lSpVal / (validLetters.length - 1) : 0;

    // Compute occupied positions
    const occ = [];
    let tOff = -(finCW / 2);
    for (var i = 0; i < validLetters.length; i++) {
      const ld = letterDims[i];
      tOff += ld.left_recess * scale;
      const lCO = tOff + ld.width * scale / 2;
      const lPos = Math.max(0, Math.min(1, cp + lCO / scaledLen));
      const occS = tOff - ld.left_recess * scale;
      const occE = tOff + ld.width * scale + ld.right_recess * scale;
      tOff += ld.width * scale + ld.right_recess * scale;
      occ.push({ position: lPos, type: 'letter',
        start: Math.max(0, Math.min(1, cp + occS / scaledLen)),
        end: Math.max(0, Math.min(1, cp + occE / scaledLen)),
        size: Math.max(ld.width, ld.height) });
      if (i < validLetters.length - 1) {
        tOff += lGap * scale;
        for (var bi = 0; bi < spPerGap; bi++) {
          const bCO = tOff + spBW * scale / 2;
          const bPos = Math.max(0, Math.min(1, cp + bCO / scaledLen));
          occ.push({ position: bPos, type: 'spacer',
            start: Math.max(0, Math.min(1, cp + tOff / scaledLen)),
            end: Math.max(0, Math.min(1, cp + (tOff + spBW * scale) / scaledLen)),
            size: spBW });
          tOff += spBW * scale;
          if (bi === 0) tOff += bSpc * scale;
        }
      }
    }

    // Step 1: Sequential left fill
    let beadIdx = _renderLeftFill(bc, beadCont, pathEl, occ, scale, pathLen, scaledLen, 0);

    // Step 2: Between-letter spacers (sequential continuing)
    let cOff = -(finCW / 2);
    for (var i = 0; i < validLetters.length; i++) {
      const ld = letterDims[i];
      cOff += ld.left_recess * scale + ld.width * scale + ld.right_recess * scale;
      if (i < validLetters.length - 1) {
        cOff += lGap * scale;
        for (var bi = 0; bi < spPerGap; bi++) {
          const bCO = cOff + spBW * scale / 2;
          const bPos = Math.max(0, Math.min(1, cp + bCO / scaledLen));
          BP.renderBlankBead(beadCont, pathEl, bPos * pathLen, bc, CONFIG.spacerBeadClass, beadIdx);
          beadIdx++;
          cOff += spBW * scale;
          if (bi === 0) cOff += bSpc * scale;
        }
      }
    }

    // Step 3: Sequential right fill (continuing index)
    _renderRightFill(bc, beadCont, pathEl, occ, scale, pathLen, scaledLen, beadIdx);

    // Step 4: Letter beads (on top)
    cOff = -(finCW / 2);
    for (var i = 0; i < validLetters.length; i++) {
      const ld = letterDims[i];
      cOff += ld.left_recess * scale;
      const lCO = cOff + ld.width * scale / 2;
      const lPos = Math.max(0, Math.min(1, cp + lCO / scaledLen));
      BP.renderLetterBead(beadCont, pathEl, validLetters[i], lPos * pathLen, bc, _letters, CONFIG.letterBeadClass);
      cOff += ld.width * scale + ld.right_recess * scale;
      if (i < validLetters.length - 1) {
        cOff += lGap * scale;
        for (var bi = 0; bi < spPerGap; bi++) { cOff += spBW * scale; if (bi === 0) cOff += bSpc * scale; }
      }
    }

    BP.applyDropShadowToContainer(container, Object.assign({}, bc, { shadow: BEAD_MINE_KIDS_SHADOW }));
  }

  // ─── Event handlers ─────────────────────────────────────────────

  function _handleInput(event) {
    if (!event.target.matches('[data-mto-field]')) return;
    const container = _getCustomizerView();
    if (container && _beadConfig) _renderBeadsWithText(container, _getMtoText(), _beadConfig);
    if (_injectedSlideId) window.RSProduct.gallery.navigate(_injectedSlideId);
  }

  function _handleInputFocus(event) {
    if (!event.target.matches('[data-mto-field]')) return;
    if (_injectedSlideId) window.RSProduct.gallery.navigate(_injectedSlideId);
  }

  function _handleVariantChange(event) {
    if (!event.data || event.data.sectionId !== window.RSProduct.sectionId) return;
    const variant = event.data.variant;
    if (!variant || !variant.title) return;
    _currentVariantTitle = variant.title;
    if (_injectedSlideId) { window.RSProduct.gallery.remove(_injectedSlideId); _injectedSlideId = null; }
    const bgUrl = _getBackgroundForVariant(_currentVariantTitle);
    if (bgUrl) _injectedSlideId = window.RSProduct.gallery.inject(bgUrl, 1);
    const container = _getCustomizerView();
    if (container && _beadConfig) _renderBeadsWithText(container, _getMtoText(), _beadConfig);
    if (_injectedSlideId) window.RSProduct.gallery.navigate(_injectedSlideId);
  }

  function _onResize() {
    clearTimeout(_resizeTimer);
    _resizeTimer = setTimeout(function () {
      const container = _getCustomizerView();
      if (container && _beadConfig) _renderBeadsWithText(container, _getMtoText(), _beadConfig);
    }, 250);
  }

  // ─── Initialization ─────────────────────────────────────────────

  function _init() {
    if (!window.RSProduct || !window.RSProduct.config.productHandle) return;
    if (!window.RSBeadPath) { console.error('[BeadMineKids] RSBeadPath not loaded'); return; }

    _fetchBaseConfig()
      .then(function (config) {
        _baseConfig = config;
        _backgroundsData = config.backgrounds || {};
        const initVarId = String(window.RSProduct.config.initialVariantId || '');
        const variantNames = config.variant_names || {};
        const fullName = variantNames[initVarId] || '';
        const parts = fullName.split(' - ');
        _currentVariantTitle = parts.length > 1 ? parts[parts.length - 1] : fullName;
        const bgUrl = _getBackgroundForVariant(_currentVariantTitle);
        if (bgUrl) _injectedSlideId = window.RSProduct.gallery.inject(bgUrl, 1);
        if (config.settings) {
          document.documentElement.style.setProperty('--customizer-path-stroke-width', (config.settings.stroke_width || 0) + 'px');
          document.documentElement.style.setProperty('--customizer-path-stroke-color', config.settings.stroke_color || 'rgba(0,0,0,0)');
        }
        return _fetchLetters();
      })
      .then(function (letters) {
        _letters = letters || {};
        _beadConfig = _buildBeadConfig();
        const container = _getCustomizerView();
        if (container && _beadConfig) _renderBeadsWithText(container, _getMtoText(), _beadConfig);
        document.addEventListener('input', _handleInput);
        document.addEventListener('focusin', _handleInputFocus);
        window.addEventListener('resize', _onResize);
        if (window.subscribe && window.theme && window.theme.PUB_SUB_EVENTS) {
          window.subscribe(window.theme.PUB_SUB_EVENTS.variantChange, _handleVariantChange);
        }
      })
      .catch(function (err) { console.error('[BeadMineKids] Init error:', err); });
  }

  document.addEventListener('DOMContentLoaded', function () { _init(); });
})();
